/**
 * useGltfHighlight — combined selection tint + drive-status blink for a
 * cloned glTF scene.
 *
 * Replaces the old `useSelectionTint` and the box-overlay
 * `DriveStatusGlow`. Both effects share the same per-mesh material
 * channel (`emissive` + `emissiveIntensity`), so they're consolidated
 * here to avoid two hooks fighting over the same uniform.
 *
 * Priority (when more than one effect is active simultaneously):
 *   driveStatus.error   →  red,    blink at 1 Hz (intensity 0.20–0.65)
 *   driveStatus.warning →  amber,  blink at 1 Hz (intensity 0.20–0.65)
 *   selected            →  user-color, **constant** intensity 0.45
 *   none                →  shared material left untouched (zero cost)
 *
 * Clone-on-write: drei's `useGLTF` cache + `cloneSkeleton` share
 * materials by reference across instances. We shallow-clone the
 * affected materials when an effect is active, mutate the clones, and
 * dispose + restore on cleanup so the highlight stays scoped.
 */

import { useEffect, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import { Color, Mesh } from 'three';
import type { Material, MeshStandardMaterial } from 'three';
import type { GLTF } from 'three-stdlib';

interface MaterialWithEmissive extends Material {
  emissive: Color;
  emissiveIntensity: number;
}

const WARNING_COLOR = '#FFB000';
const ERROR_COLOR = '#E03030';
const SELECTION_INTENSITY = 0.45;
const BLINK_LO = 0.2;
const BLINK_HI = 0.65;
const BLINK_HZ = 1;

interface DriveStatus {
  warning?: boolean;
  error?: boolean;
}

interface UseGltfHighlightOpts {
  selected?: boolean;
  selectionColor?: string;
  highlightColor?: string;
  driveStatus?: DriveStatus;
  showWarnings?: boolean;
  showErrors?: boolean;
}

type Mode =
  | { kind: 'none' }
  | { kind: 'selected'; color: string }
  | { kind: 'blink'; color: string };

function pickMode({
  selected,
  selectionColor,
  highlightColor,
  driveStatus,
  showWarnings,
  showErrors,
}: UseGltfHighlightOpts): Mode {
  if (driveStatus?.error && showErrors !== false) {
    return { kind: 'blink', color: ERROR_COLOR };
  }
  if (driveStatus?.warning && showWarnings !== false) {
    return { kind: 'blink', color: WARNING_COLOR };
  }
  if (selected && selectionColor) {
    return { kind: 'selected', color: selectionColor };
  }
  if (highlightColor) {
    return { kind: 'selected', color: highlightColor };
  }
  return { kind: 'none' };
}

export function useGltfHighlight(
  gltf: GLTF | null,
  opts: UseGltfHighlightOpts,
): void {
  const mode = pickMode(opts);

  // Stable mode signature drives the clone/restore lifecycle.
  const signature =
    mode.kind === 'none' ? 'none' : `${mode.kind}:${mode.color}`;

  // List of cloned materials we currently own — read by the per-frame
  // updater so it doesn't have to traverse the scene every frame.
  const cloned = useRef<MaterialWithEmissive[]>([]);

  useEffect(() => {
    if (!gltf || mode.kind === 'none') return;
    const tint = new Color(mode.color);
    type Swap = {
      mesh: Mesh;
      original: Material | Material[];
      cloned: Material | Material[];
    };
    const swaps: Swap[] = [];
    const list: MaterialWithEmissive[] = [];
    const cloneOne = (m: Material): Material => {
      const c = m.clone();
      if ('emissive' in c) {
        const mat = c as MeshStandardMaterial;
        mat.emissive.copy(tint);
        mat.emissiveIntensity = SELECTION_INTENSITY;
        list.push(mat as unknown as MaterialWithEmissive);
      }
      return c;
    };
    gltf.scene.traverse((obj) => {
      const mesh = obj as Mesh;
      if (!mesh.isMesh || !mesh.material) return;
      const next: Material | Material[] = Array.isArray(mesh.material)
        ? mesh.material.map(cloneOne)
        : cloneOne(mesh.material);
      swaps.push({ mesh, original: mesh.material, cloned: next });
      mesh.material = next;
    });
    cloned.current = list;
    return () => {
      for (const { mesh, original, cloned: c } of swaps) {
        mesh.material = original;
        if (Array.isArray(c)) c.forEach((m) => m.dispose());
        else c.dispose();
      }
      cloned.current = [];
    };
  }, [gltf, signature]);

  // Per-frame blink loop. Runs only when in 'blink' mode; otherwise the
  // emissiveIntensity set in the effect above stays put.
  // Calls invalidate() to keep the render loop alive during blinking
  // when the canvas uses `frameloop="demand"`.
  useFrame(({ clock, invalidate }) => {
    if (mode.kind !== 'blink' || cloned.current.length === 0) return;
    const t = clock.getElapsedTime();
    const phase = 0.5 + 0.5 * Math.sin(2 * Math.PI * BLINK_HZ * t);
    const intensity = BLINK_LO + (BLINK_HI - BLINK_LO) * phase;
    for (const m of cloned.current) m.emissiveIntensity = intensity;
    invalidate();
  });
}
